import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, filter, map, merge, of, takeUntil, timer, Subscription, Subject, combineLatest } from 'rxjs';

import type { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import {
  selectLatestCopyInformation,
  selectLatestCreatedId,
  selectLatestDeletedIdList,
  selectProjectEntityTree,
  selectProjectEntityLoading,
  LatestCopyInformation,
  selectProjectFeedbackMessages,
  selectTemporaryHoodlineFeedbackMessages
} from '../../store/project-data/project-data.selectors';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import {
  selectHoodSettingOpened,
  selectOverlappingLineIds,
  selectUIProductSidebarOpened,
  selectUIProductSidebarRightOpened
} from '../../store/product-view/product-view.selectors';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import type { TreeEntity } from '../../../core/models/entity.model';
import { EntityTypesActions } from '../../../core/store/entity-types/entity-types.actions';
import { selectEntityTypesState } from '../../../core/store/entity-types/entity-types.selectors';
import { TranslateService } from '@ngx-translate/core';
import { ProjectEntityService } from '../../../core/services/project.service';
import { withLatestFrom } from 'rxjs/operators';
import { ProductViewActions } from '../../store/product-view/product-view.actions';
import { ActiconInteractiveComponentHandler } from '../../../visualize3d/v3d-components/acticon-interactive/acticon-interactive-component-handler';
import { select3DViewStatus, VisualProductViewStatus } from '../../store/project-data/project-data-3d.selectors';
import { PropertyGroupType } from 'src/app/core/models/property-group-type.model';
import { ProductContextActions } from '../../store/product-context/product-context.actions';
import { EntityTypeKey } from 'src/app/shared/constants/entity-type-keys';
import { VisualEditorLineUpdatedEvent } from '../../components/product-visual-editor/models/visual-editor-line-updated-event.model';
import { Property } from 'src/app/core/models/property.model';

@Component({
  selector: 'app-addline-view',
  templateUrl: './addline-view.component.html',
  styleUrls: ['./addline-view.component.scss'],
  standalone: false
})
export class AddLineViewComponent implements OnInit, OnDestroy {
  projectEntity$: Observable<TreeEntity>;
  projectTreeEntity$: Observable<TreeEntity>;
  loading$: Observable<boolean>;
  showLoading$ = of(false);
  entityTypeNames$: Observable<Record<string, string>>;
  hoodSettingsOpened$: Observable<boolean>;
  panelErrorIds$: Observable<string[]>;

  overlappingLineIds$: Observable<string[]>;

  latestCreatedId$: Observable<string | null>;
  latestCopiedId$: Observable<LatestCopyInformation>;
  latestDeletedIdList$: Observable<string[]>;

  show3DStatus$: Observable<VisualProductViewStatus>;

  overlapMessages$: Observable<TreeEntity[]>;

  projectId = '';
  productId = '';
  projectName = '';
  breadcrumbs: Breadcrumb[];
  sidebarOpened$: Observable<boolean>;
  sidebarRightOpened$: Observable<boolean>;

  sidenavSettings = {
    fixed: true,
    top: 157,
    bottom: 0,
    height: 100,
    maxHeightProductTree: 100
  };

  headerHeight = 157;
  footerHeight = 160;

  visualEditorPadding = 0;

  showInSidebar: boolean;

  private subscriptions: Subscription[] = [];

  private entity3DClicked$ = new Subject<string>();

  @ViewChild('messageBox', { read: ElementRef }) messageBox!: ElementRef;

  @HostListener('window:resize') onWindowResize() {
    this.setSidenavSettings();
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    breakpointObserver: BreakpointObserver,
    private translateService: TranslateService,
    private projectEntityService: ProjectEntityService,
    private interactiveComponentHandler: ActiconInteractiveComponentHandler
  ) {
    this.productId = this.route.snapshot.queryParamMap.get('selectedId');

    this.store.dispatch(ProductContextActions.set_context({ context: 'Hoodline' }));
    this.subscriptions.push(
      this.router.events.subscribe((e: any) => {
        if (e instanceof NavigationEnd) {
          this.initializeComponent();
        }
      })
    );
    breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium]).subscribe(result => {
      if (result.matches) {
        this.showInSidebar = false;
      }
    });
    breakpointObserver.observe([Breakpoints.Large, Breakpoints.XLarge]).subscribe(result => {
      if (result.matches) {
        this.showInSidebar = true;
      }
    });

    this.projectEntity$ = this.store.select(selectProjectEntityTree).pipe(
      withLatestFrom(this.store.select(selectEntityTypesState)),
      map(([project, entityTypesState]) => {
        if (!project || !entityTypesState.loaded) {
          return null;
        }
        return this.projectEntityService.sortEntityChildren(project, entityTypesState.entityTypes, 0);
      }),
      filter(value => !!value)
    );

    this.subscriptions.push(
      this.projectEntity$.subscribe(f => {
        this.projectName = f.Children.find(hood => hood.Id === this.productId)?.Properties['Name']?.Value as string;
      })
    );

    this.loading$ = this.store.select(selectProjectEntityLoading);

    this.latestCreatedId$ = this.store.select(selectLatestCreatedId);
    this.latestCopiedId$ = this.store.select(selectLatestCopyInformation);
    this.latestDeletedIdList$ = this.store.select(selectLatestDeletedIdList);

    this.sidebarOpened$ = this.store.select(selectUIProductSidebarOpened);
    this.sidebarRightOpened$ = this.store.select(selectUIProductSidebarRightOpened);

    this.show3DStatus$ = this.store.select(select3DViewStatus);

    this.hoodSettingsOpened$ = this.store.select(selectHoodSettingOpened);

    this.overlappingLineIds$ = this.store.select(selectOverlappingLineIds);

    this.entityTypeNames$ = this.store.select(selectEntityTypesState).pipe(
      map((state): Record<string, string> => {
        if (!state.loaded) {
          return {};
        }

        const names: Record<string, string> = {};

        for (const entityTypeKey in state.entityTypes) {
          names[entityTypeKey] = this.translateService.instant(state.entityTypes[entityTypeKey].NameId);
        }
        return names;
      })
    );

    this.overlapMessages$ = this.store.select(selectOverlappingLineIds).pipe(
      withLatestFrom(this.projectEntity$),
      map(([overlappingLineIds, projectEntity]) => {
        const messsages = overlappingLineIds.map(id => {
          const entity = this.findInTreeById(projectEntity, id);

          return {
            Id: '',
            EntityTypeKey: 'Message',
            ParentId: id,
            Groups: {},
            Properties: {
              Level: {
                Key: 'Level',
                Value: 1
              } as Property,
              TextId: {
                Key: 'TextId',
                Value: 'GastroFlowHoodLine.Error.HoodLinesOverlapping'
              } as Property,
              Text: {
                Key: 'Text',
                Value: null
              } as Property,
              PropertyKey: {
                Key: 'PropertyKey',
                Value: 'PositionX'
              } as Property
            },
            Children: [],
            Parent: entity
          } as TreeEntity;
        });

        return messsages.filter(m => m.Parent?.ParentId === this.productId);
      })
    );

    this.panelErrorIds$ = this.store
      .select(selectTemporaryHoodlineFeedbackMessages)
      .pipe(map(messages => messages.filter(m => m.Parent.EntityTypeKey === EntityTypeKey.GastroFlowOutsidePanel).map(m => m.ParentId)));

    const resultReceived = this.loading$.pipe(filter(result => !result));

    this.showLoading$ = merge(
      timer(350).pipe(
        map(() => true),
        takeUntil(resultReceived)
      ),
      resultReceived.pipe(map(() => false))
    );

    setTimeout(() => {
      this.setSidenavSettings();
    });

    this.subscriptions.push(
      this.entity3DClicked$.pipe().subscribe(clickedEntityId => {
        this.store.dispatch(ProductViewActions.toggle_hoodline({ id: clickedEntityId, closeOthers: false }));

        this.interactiveComponentHandler.toggleSelectedId(clickedEntityId);
      })
    );

    // Update the product data after adding a new line
    this.subscriptions.push(
      this.store.select(selectLatestCreatedId).subscribe(id => {
        if (id !== null) {
          this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
        }
      })
    );

    // Update the product data after deleting a line
    this.subscriptions.push(
      this.store.select(selectLatestDeletedIdList).subscribe(id => {
        if (id.length > 0) {
          this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
        }
      })
    );

    // Update the padding for 3d editor after message list change
    this.subscriptions.push(
      combineLatest([this.store.select(selectProjectFeedbackMessages), this.overlappingLineIds$]).subscribe(() => {
        setTimeout(() => {
          if (this.messageBox) {
            this.visualEditorPadding = this.messageBox.nativeElement.offsetHeight;
          }
        });
      })
    );
  }

  addLine() {
    this.store.dispatch(
      ProjectDataActions.create_entity_start({
        projectId: this.projectId,
        entityId: this.productId,
        productTypeId: 'GastroFlowHoodLine',
        productName: null,
        defaultValues: []
      })
    );
  }

  addConnectionLine() {
    this.store.dispatch(
      ProjectDataActions.create_entity_start({
        projectId: this.projectId,
        entityId: this.productId,
        productTypeId: 'GastroFlowHoodConnectionLine',
        productName: null,
        defaultValues: []
      })
    );
  }

  shouldRenderGroupFilter(group: PropertyGroupType): boolean {
    if (group.Key.includes('ConfigureHoodLines') && !group.Key.includes('ConfigureHoodLinesButtonGroup')) {
      return true;
    }
    return false;
  }

  shouldRenderButtonGroup(group: PropertyGroupType): boolean {
    if (group.Key.includes('ConfigureHoodLinesButtonGroup')) {
      return true;
    }
    return false;
  }

  isHoodLineMessage(message: TreeEntity): boolean {
    return (
      message.Parent?.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine ||
      message.Parent?.EntityTypeKey === EntityTypeKey.GastroFlowHoodConnectionLine ||
      message.Parent?.EntityTypeKey === EntityTypeKey.GastroFlowOutsidePanel
    );
  }

  setSidenavSettings() {
    /* 8 is top and bottom margins */
    this.sidenavSettings.height = document.body.parentElement.offsetHeight - this.sidenavSettings.top - this.sidenavSettings.bottom - 8;

    document.addEventListener('scroll', () => {
      /* When opening element in overlay (e.g. dialog), scrollTop and scrollHeight changes values.
       * Therefore, topPosition and topMax values are added.
       * Also, instead of using document.body.parentElement.scrollHeight, document.body.scrollHeight is used.
       */
      const overlayElement = document.querySelector('.cdk-global-scrollblock');
      let topPosition = 0;
      if (overlayElement) {
        topPosition = parseFloat(window.getComputedStyle(overlayElement).top.slice(0, -2));
      }

      this.sidenavSettings.top = Math.max(0, this.headerHeight - (document.body.parentElement.scrollTop + topPosition * -1));
      const topMax = Math.max(document.body.parentElement.scrollTop, -topPosition);
      this.sidenavSettings.bottom = Math.max(
        0,
        topMax + document.body.parentElement.offsetHeight + this.footerHeight - document.body.scrollHeight
      );
      this.sidenavSettings.height = document.body.parentElement.offsetHeight - this.sidenavSettings.top - this.sidenavSettings.bottom;
    });
  }

  initializeComponent() {
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.store.dispatch(ProjectDataActions.get_project_start({ id: this.projectId }));
  }

  ngOnInit(): void {
    this.setBreadcrumbs();
    this.store.dispatch(EntityTypesActions.entity_types_required());
  }

  ngOnDestroy(): void {
    this.store.dispatch(ProductViewActions.set_overlapping_line_ids({ lineIds: [] }));
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  entity3DClicked(entityId: string): void {
    this.entity3DClicked$.next(entityId);
  }

  toggleHoodSettings(opened: boolean) {
    this.store.dispatch(ProductViewActions.change_hood_settings_opened({ opened }));
  }

  editorUpdate(event: VisualEditorLineUpdatedEvent) {
    event.updatedLines.forEach(line => {
      this.store.dispatch(ProjectDataActions.update_hood_line_positions({ entityId: line.entityId, position: line.position }));
    });
  }

  getHoodVersion(projectEntity: TreeEntity) {
    const hoodEntity = projectEntity.Children.find(x => x.Id === this.productId);

    return hoodEntity && (hoodEntity.Properties['Version']?.Value as number);
  }

  backButtonClicked() {
    this.store.dispatch(ProjectDataActions.reset_latest_created_id());
    this.store.dispatch(ProjectDataActions.reset_latest_deleted_id());
    this.router.navigate([`/projects/${this.projectId}/products`], { queryParams: { selectedId: this.productId } });
  }

  setBreadcrumbs() {
    this.projectEntity$.subscribe((projectEntity: TreeEntity) => {
      const projectBreadcrumb: Breadcrumb = { url: `/projects/${this.projectId}`, text: '', translatable: false, isCurrent: false };
      const nameProperty = projectEntity?.Properties['Name'];
      const name = (nameProperty?.Value as string) || '';

      if (name) {
        projectBreadcrumb.text = name;
        projectBreadcrumb.translatable = false;
      } else {
        projectBreadcrumb.text = '-';
        projectBreadcrumb.translatable = false;
      }
      this.breadcrumbs = [
        { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
        projectBreadcrumb,
        {
          url: `/projects/${this.projectId}/products`,
          urlParams: { selectedId: this.productId },
          text: 'UI.Common.Breadcrumb.Products',
          translatable: true,
          isCurrent: false
        },
        {
          url: `/projects/${this.projectId}/products/addline`,
          text: 'UI.Common.Breadcrumb.Products.AddLine',
          translatable: true,
          isCurrent: true
        }
      ];
    });
  }

  getHoodlines(projectEntity: TreeEntity): TreeEntity[] {
    return this.findInTreeById(projectEntity, this.productId).Children.filter(
      f => f.EntityTypeKey === EntityTypeKey.GastroFlowHoodLine || f.EntityTypeKey === EntityTypeKey.GastroFlowHoodConnectionLine
    );
  }

  findInTreeById(selector: TreeEntity, id: string): TreeEntity {
    if (selector === null) {
      return null;
    }
    if (selector.Id === id) {
      return selector;
    }
    for (const child of selector.Children) {
      const candidate = this.findInTreeById(child, id);
      if (candidate !== null) {
        return candidate;
      }
    }
    return null;
  }
}
